import api from './api';

/**
 * Service for interacting with audit log endpoints
 */
class AuditService {
  /**
   * New unified audit fetch (admin)
   */
  static async fetchAudit({ resource, action, userId, search, startDate, endDate, limit = 50, skip = 0 } = {}) {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (resource) params.append('resource', resource);
    if (action) params.append('action', action);
    if (userId) params.append('userId', userId);
    if (search) params.append('search', search);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return api.get(`/audit?${params.toString()}`);
  }

  /**
   * Fetch recent login events
   */
  static async fetchLoginEvents({ userId, limit = 50, skip = 0 } = {}) {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (userId) params.append('userId', userId);
    return api.get(`/audit/logins?${params.toString()}`);
  }

  /**
   * Fetch a single audit log detail
   */
  static async getAuditLogDetail(id) {
    return api.get(`/audit/${id}`);
  }
  /**
   * Get audit logs for a specific resource
   * @param {string} resource - Resource type (Patient, PatientQueue, etc.)
   * @param {string} resourceId - Resource ID
   * @param {Object} options - Query options
   * @returns {Promise} API response
   */
  static async getAuditLogs(resource, resourceId, options = {}) {
    try {
      const { limit = 50, skip = 0, startDate, endDate, action } = options;
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString()
      });
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (action) params.append('action', action);
      
      const response = await api.get(`/screening/audit/${resource}/${resourceId}?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  /**
   * Get audit log summary/statistics
   * @param {Object} filters - Filter options
   * @returns {Promise} API response
   */
  static async getAuditSummary(filters = {}) {
    try {
      const { resource, startDate, endDate } = filters;
      
      const params = new URLSearchParams();
      if (resource) params.append('resource', resource);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/screening/audit/summary?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching audit summary:', error);
      throw error;
    }
  }

  /**
   * Get recent audit activity
   * @param {Object} options - Query options
   * @returns {Promise} API response
   */
  static async getRecentActivity(options = {}) {
    try {
      const { limit = 20, resource } = options;
      
      const params = new URLSearchParams({
        limit: limit.toString()
      });
      
      if (resource) params.append('resource', resource);
      
      const response = await api.get(`/screening/audit/recent?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  }

  /**
   * Get audit logs for a patient (convenience method)
   * @param {string} patientId - Patient ID
   * @param {Object} options - Query options
   * @returns {Promise} API response
   */
  static async getPatientAuditLogs(patientId, options = {}) {
    return this.getAuditLogs('Patient', patientId, options);
  }

  /**
   * Get audit logs for a queue entry (convenience method)
   * @param {string} queueId - Queue ID
   * @param {Object} options - Query options
   * @returns {Promise} API response
   */
  static async getQueueAuditLogs(queueId, options = {}) {
    return this.getAuditLogs('PatientQueue', queueId, options);
  }

  /**
   * Export audit logs as CSV
   * @param {Object} params - Export parameters (startDate, endDate)
   * @returns {Promise} API response with blob data
   */
  static async exportLogs(params = {}) {
    const queryParams = {};
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;
    if (params.resource) queryParams.resource = params.resource;
    if (params.action) queryParams.action = params.action;
    if (params.userId) queryParams.userId = params.userId;
    if (params.search) queryParams.search = params.search;
    return api.getBlob('/screening/exports/audit-logs', queryParams);
  }
}

export default AuditService;
